import type { JSX, ReactNode } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import { signOutTaki } from '@taki/auth';
import type { TakiClaims, User } from '@taki/auth';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusBadge,
} from '@taki/ui';
import { formatPlatformVersion, useBuildStamp } from '../buildStamp.js';
import type { BuildStampState } from '../buildStamp.js';

export interface AccountScreenProps {
  /** Config runtime du client (branding, orgId…). */
  config: TakiConfig;
  /** Utilisateur Firebase connecte. */
  user: User;
  /** Claims du token (orgId, orgRole). */
  claims: TakiClaims;
}

/** Premiere lettre de l'email (ou du displayName), en majuscule. Fallback "?". */
function initialOf(user: User): string {
  const source = user.email ?? user.displayName ?? '';
  const first = source.trim().charAt(0);
  return first ? first.toUpperCase() : '?';
}

/** Ligne cle/valeur d'une carte : libelle discret a gauche, valeur a droite. */
function InfoRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex min-h-[1.75rem] items-center justify-between gap-4">
      <span className="text-sm text-ink-500">{label}</span>
      <div className="flex min-w-0 items-center gap-2 text-right">{children}</div>
    </div>
  );
}

/** Ce que la ligne "Version" affiche, deduit de l'etat de lecture du tampon. */
interface VersionDisplay {
  /** La valeur a l'ecran. Jamais un numero invente, jamais un `0.0.0` de repli. */
  text: string;
  /** L'infobulle : d'ou vient ce texte, ou pourquoi il n'y a pas de numero. */
  title: string;
  /** Vrai quand ce n'est pas un numero mais un constat : encre discrete. */
  muted: boolean;
}

/**
 * La ligne "Version" pour chacun des quatre etats du tampon de build.
 *
 * `null` = lecture en cours : la ligne garde sa hauteur et reste VIDE le temps
 * du chargement, plutot que d'afficher un texte qui serait remplace une frame
 * plus tard (meme choix que la bande de version du menu DEV).
 *
 * Un tampon ABSENT n'est pas une panne : un deploiement fabrique avant l'arrivee
 * du tampon n'en a pas, et le serveur de dev n'en sert pas toujours un. La ligne
 * le DIT alors en clair ; un vide se lirait comme un bug. La raison technique,
 * elle, reste dans l'infobulle.
 */
function versionDisplayOf(state: BuildStampState): VersionDisplay | null {
  switch (state.kind) {
    case 'loading':
      return null;
    case 'found':
      return {
        text: formatPlatformVersion(state.stamp.platformVersion),
        title: `Tampon ${
          state.stamp.mode === 'serve' ? 'servi par le serveur de dev' : 'du site fabrique'
        }, ${state.stamp.generatedAt}`,
        muted: state.stamp.platformVersion == null,
      };
    case 'missing':
      return {
        text: 'non disponible',
        title: `Aucun tampon de build : ${state.reason}`,
        muted: true,
      };
    case 'error':
      return { text: 'tampon illisible', title: state.message, muted: true };
  }
}

/** Petite etiquette neutre "bientot" pour les valeurs encore indisponibles. */
function SoonTag(): JSX.Element {
  return (
    <span className="inline-flex h-[1.375rem] items-center rounded-pill border border-border-soft bg-surface-1 px-2.5 text-[0.6875rem] font-medium text-ink-400">
      bientot
    </span>
  );
}

/**
 * Ecran "Mon compte". Identite de l'utilisateur, environnement (organisation et
 * role issus des claims, version qui tourne), preferences (a venir) et
 * deconnexion. Sobre et neutre : le lime n'apparait que sur le badge de role.
 *
 * LA VERSION NE S'ECRIT PAS ICI, ELLE SE LIT (`docs/VERSIONING.md`) : elle vient
 * du tampon de build `/taki-build.json`, par `useBuildStamp`. C'est le premier
 * endroit de l'interface, hors greffe DEV, qui dise ce qui tourne chez un
 * client : la question qu'on se pose en premier quand un client dit "ca ne
 * marche pas", et qui n'avait aucune reponse a l'ecran.
 */
export function AccountScreen({ config, user, claims }: AccountScreenProps): JSX.Element {
  const displayName = user.displayName ?? user.email ?? 'Utilisateur';
  const buildStamp = useBuildStamp();
  const version = versionDisplayOf(buildStamp);

  return (
    <div className="flex flex-col gap-6">
      {/* Profil : avatar + identite. */}
      <Card>
        <CardContent className="flex items-center gap-4 p-6">
          <Avatar size={46} initials={initialOf(user)} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-base font-semibold tracking-[-0.01em] text-ink-900">
              {displayName}
            </span>
            <span className="truncate text-sm text-ink-500">Utilisateur</span>
          </div>
        </CardContent>
      </Card>

      {/* Environnement : instance, role, version qui tourne. */}
      <Card>
        <CardHeader>
          <CardTitle>Environnement</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3.5">
          <InfoRow label="Organisation">
            <span className="truncate font-mono text-sm text-ink-700">{config.orgId}</span>
          </InfoRow>
          <InfoRow label="Role">
            {claims.orgRole ? (
              <StatusBadge label={claims.orgRole} tone="accent" />
            ) : (
              <span className="text-sm text-ink-400">non defini</span>
            )}
          </InfoRow>
          <InfoRow label="Version">
            {version != null && (
              <span
                className={`truncate font-mono text-sm ${
                  version.muted ? 'text-ink-400' : 'text-ink-700'
                }`}
                title={version.title}
              >
                {version.text}
              </span>
            )}
          </InfoRow>
        </CardContent>
      </Card>

      {/* Preferences : a venir. */}
      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <InfoRow label="Theme, notifications, fuseau">
            <SoonTag />
          </InfoRow>
        </CardContent>
      </Card>

      <div>
        <Button
          variant="danger"
          onClick={() => {
            void signOutTaki();
          }}
        >
          Se deconnecter
        </Button>
      </div>
    </div>
  );
}
